import {
  type ChangeEvent,
  type FocusEventHandler,
  type ReactNode,
  useCallback,
} from 'react'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { SafeFormattedText } from '@/components/ui/safe-formatted-message.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption'
import { classNames } from '@/utils/helpers.ts'

export interface HourInputSectionProps {
  value: number | undefined
  onChange?: (value: number) => void
  onBlur?: FocusEventHandler<HTMLInputElement>
  title: ReactNode
  description: ReactNode
  unit?: 'Hours' | 'Minutes' | 'Seconds' | string
  textClassName?: string
  className?: string
  errorText?: string | null
  min?: number
  max?: number
  disabled?: boolean
}

export const TimeInputSection = (props: HourInputSectionProps) => {
  const {
    title,
    description,
    value,
    onChange,
    onBlur,
    min = 0,
    max,
    unit,
    textClassName,
    disabled,
    errorText,
  } = props

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const v = Number(event.target.value)

      return onChange?.(isNaN(v) ? 0 : v)
    },
    [onChange],
  )

  return (
    <SettingsOption
      title={title}
      description={description}
      mobileVertical
      biggerTitle
    >
      <div className="flex gap-2">
        <TextField
          type="text"
          className={classNames(textClassName, {
            'border-red-500': !!errorText,
          })}
          value={value}
          onChange={disabled ? undefined : handleChange}
          onBlur={onBlur}
          disabled={disabled}
          min={min}
          max={max}
          showSuffixSeparator
          suffix={<span className="text-secondary-300">{unit ?? 'Hours'}</span>}
        />
        {errorText && (
          <Tooltip
            content={
              <span className="text-red-500 text-sm">
                <SafeFormattedText text={errorText} />
              </span>
            }
            contentClassName="whitespace-pre-wrap text-left"
          >
            <InfoIcon className="text-red-500 h-4 w-4 cursor-pointer" />
          </Tooltip>
        )}
      </div>
    </SettingsOption>
  )
}
